import { useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import {
  Alert,
  Box,
  IconButton,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Pagination,
  FormControl, 
  Select, 
  MenuItem,
} from "@mui/material";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import FormModal from "./FormModal";
import { useDispatch, useSelector } from "react-redux";
import React from "react";
import { signedInUserEmail } from '../features/userAuths/userAuthsSlice.js';
import { getUserId } from "../features/userAuths/userAuthsSlice.js";
import {
  fetchHomeTableList,
  selectHomeTableList,
  selectHomeTableStatus,
  selectCurrentPage,
  selectPageSize,
  selectTotalCount,
  setCurrentPage,
  setSearchQuery,
  setShowMine,
  setPageSize,
  selectSearchQuery,
  selectShowMine,
} from "../features/home/homeTableSlice";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import ExportPopUp from "./ExportPopUp";
import Loader from "./Loader";
import { CSVLink } from "react-csv";
import DownloadIcon from "@mui/icons-material/Download";
import { updateComicStage } from "../api/homeTableData.js";
import { retryTranslationPost } from "../api/submitJobPost.js";

function escapeRegExp(value) {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
}

function QuickSearchToolbar(props) {
  const userEmail = useSelector(signedInUserEmail);
  const csvData = React.useMemo(() => {
    if (!props.rows || props.rows.length === 0) return [];
    return props.rows.map(row => ({
      ...row,
      user_email: userEmail
    }));
  }, [props.rows, userEmail]);
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Typography variant="h4" fontWeight="bold">
        Comic Issues
      </Typography>
      <Box
        display="flex"
        borderRadius="5px"
        border="1px solid #4b4b4b"
        width="30rem"
        padding=".4rem"
      >
        <Tooltip title="Search">
          <IconButton type="button">
            <SearchIcon />
          </IconButton>
        </Tooltip>
        <InputBase
          sx={{ ml: 2, flex: 1 }}
          placeholder="Quick Search..."
          value={props.value}
          onChange={props.onChange}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" />,
            endAdornment: (
              <Tooltip title="Clear Input">
                <IconButton
                  title="Clear"
                  aria-label="Clear"
                  size="small"
                  style={{ visibility: props.value ? "visible" : "hidden" }}
                  onClick={props.clearSearch}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ),
          }}
        />
      </Box>
      <Box display="flex" alignItems="center" gap={2}>
        <FormControlLabel
          control={
            <Switch
              checked={!props.showMine}
              onChange={props.toggleShowMine}
              color="primary"
            />
          }
          label={props.showMine ? "Show All" : "Show Mine"}
        >
        </FormControlLabel>
        <CSVLink
          data={csvData}
          filename={`${userEmail}-comics-report.csv`}
          className="export-button"
          separator={","}
          style={{ textDecoration: 'none' }}
        >
          <Button
            size="large" variant="contained"
          >
            <DownloadIcon />
            Export Report
          </Button>
        </CSVLink>

        {props.createButton === "true" && <FormModal showMine={props.showMine} userId={props.userId}  />}
      </Box>
    </Box>
  );
}

const HomeTable = ({ createButton, sortData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const homeTable = useSelector(selectHomeTableList);
  const homeTableStatus = useSelector(selectHomeTableStatus);
  const userId = useSelector(getUserId)
  const currentPage = useSelector(selectCurrentPage);
  const pageSize = useSelector(selectPageSize);
  const totalCount = useSelector(selectTotalCount);
  const searchQuery = useSelector(selectSearchQuery);
  const showMine = useSelector(selectShowMine);
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [localSearchText, setLocalSearchText] = useState(searchQuery);
  const [searchText, setSearchText] = useState("");
  const [rows, setRows] = useState([]);
  const [isChange, setIsChange] = useState(false);
  const [comicId, setComicId] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [count, setCount] = useState(false);

  const [confirmOpen,setConfirmOpen] = useState(false);
  const [rowToConfirm,setRowToConfirm] = useState(null);
  const [confirmLoading,setConfirmLoading] = useState(false);

  const FRONT_TO_BACK_STAGE = {
  INIT: "stage-initialization",
  Extraction: "text-extraction",
  Translation: "ai-translation",
  Export: "output-generation",
  Enrichment: "context-extraction",
  Editing: "editing",
};

const PREVIOUS_STAGE_MAP = {
  Editing: "Translation",
  Translation: "Extraction",
};

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (localSearchText !== searchQuery) {
        dispatch(setSearchQuery(localSearchText));
      }
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [localSearchText, searchQuery, dispatch]);

  useEffect(() => {
    const params = {
      page: currentPage,
      page_size: pageSize,
      search: searchQuery,
      user_id: showMine ? userId : undefined,
      stage: sortData ? FRONT_TO_BACK_STAGE[sortData] : undefined,
    };
    dispatch(fetchHomeTableList(params));
  }, [currentPage, pageSize, searchQuery, showMine, sortData, userId, dispatch]);


  useEffect(() => {
    setIsLoading(homeTableStatus === "loading");
  }, [homeTableStatus]);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      setOpenSnackbar(false);
      return;
    }
    setOpenSnackbar(false);
  };

  const handleExportClose = () => {
    setOpen(false);
  };

  const handleClick = (event) => {
    const currStage = event.row.stage.toLowerCase();
    const currStatus = event.row.status.toLowerCase();
    const currId = event.row.id;
    setComicId(currId);
    if (
      (currStage === "extraction" || currStage === "translation" || currStage === "editing") &&
      (currStatus === "reviewing" || currStatus === "ready for review")
    ) {
      navigate(`/${currStage}/${currId}`);
    } else {
      setOpenSnackbar(true);
    }
  };

  const handleConfirm = async()=>{
    if(!rowToConfirm) return;
    const prevStage = PREVIOUS_STAGE_MAP[rowToConfirm.stage];
    setConfirmLoading(true);
    try {
      await updateComicStage(rowToConfirm.id, FRONT_TO_BACK_STAGE[prevStage]);
      dispatch(fetchHomeTableList());
      setConfirmOpen(false);  
      setRowToConfirm(null);
    } catch (error) {
      console.error("Failed to move to previous stage");
    }
    finally{
      setConfirmLoading(false);
    }
  }

  const handlePageChange = (event, value) => {
    dispatch(setCurrentPage(value));
  };

  const handleToggleShowMine = () => {
    dispatch(setShowMine(!showMine));
  };

  const handleClearSearch = () => {
    setLocalSearchText("");
    dispatch(setSearchQuery(""));
  };

  const handlePageSizeChange = (event) =>{
        const newPageSize = event.target.value;
        dispatch(setPageSize(newPageSize));
  }

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5 },
    { field: "title", headerName: "Title", flex: 1 },
    {
      field: "issue",
      headerName: "Issue",
      flex: 1,
    },
    {
      field: "file_name",
      headerName: "File Name",
      type: "string",
      headerAlign: "left",
      align: "left",
      flex: 2,
    },
    {
      field: "volume",
      headerName: "Volume",
      type: "number",
      align: "left",
      headerAlign: "left",
      flex: 0.5,
    },
    {
      field: "L2R",
      headerName: "Reading Direction",
      flex: 0.8,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row: { L2R } }) => {
        return (
          <Box
            width="80%"
            p="5px"
            display="flex"
            justifyContent="center"
            backgroundColor={L2R ? "#e6effc" : "#ffe5ee"}
            borderRadius="5px"
          >
            <Typography color={L2R ? "#0764E6" : "#A00"}>
              {L2R ? "Left to Right" : "Right to Left"}
            </Typography>
          </Box>
        );
      },
    },
    // {
    //   field: "episode",
    //   headerName: "Episode",
    //   type: "number",
    //   headerAlign: "left",
    //   align: "left",
    //   flex: 1,
    // },
    {
      field: "createdDate",
      headerName: "Created Date",
      flex: 0.5,
    },
    {
      field: "lastUpdated",
      headerName: "Last Updated",
      flex: 0.5,
    },
    {
      field: "stage",
      headerName: "Stage",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row: { stage } }) => {
        return (
          <Box
            width="60%"
            p="5px"
            display="flex"
            justifyContent="center"
            backgroundColor={
              stage === "Translation"
                ? "#e6effc"
                : stage === "Extraction"
                  ? "#ffe5ee"
                  : "#fff8e7"
            }
            borderRadius="5px"
          >
            <Typography
              color={
                stage === "Translation"
                  ? "#0764E6"
                  : stage === "Extraction"
                    ? "#A00"
                    : "#D5B500"
              }
            >
              {stage}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
  ];
if (sortData !== "Extraction") {
  columns.splice(columns.length-1,0,{
    field: "actions",
    headerName: "Actions",
    flex: 1,
    sortable: false,
    filterable: false,
    align: "center",
    headerAlign: "center",
    renderCell: ({ row }) => {
      const prevStage = PREVIOUS_STAGE_MAP[row.stage];
      const showRetry = row.stage === "Translation" && row.status === "Error";
      return (
        <Box sx={{ display: "flex", gap: 1 }}>
          {showRetry ?(
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await retryTranslationPost(row.id);
                  dispatch(fetchHomeTableList({
                    page: currentPage,
                    page_size: pageSize,
                    search: searchQuery,
                    user_id: showMine ? userId : undefined,
                    stage: sortData ? FRONT_TO_BACK_STAGE[sortData] : undefined,
                  }));
                } catch (error) {
                  console.error("Failed to retry translation", error);
                }
              }}
            >
              Retry
            </Button>
          ):
          (prevStage && (
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              onClick={async (e) => {
                e.stopPropagation();
                setRowToConfirm(row);
                setConfirmOpen(true);
              }}
            >
              Go to {prevStage}
            </Button>
          ))
          }
        </Box>
      );
    },
  })
}

  if (isLoading && homeTable.length === 0) {
    return <Loader />;
  }
  const totalPages = Math.ceil(totalCount / pageSize);
  return (
    <Paper
      sx={{
        borderRadius: "5px",
        padding: "1.5rem",
        height: createButton ? "65vh" : "75vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Snackbar
        open={openSnackbar}
        autoHideDuration={2500}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert 
        onClose={handleClose} 
        severity="warning" 
        sx={{ width: "100%" }}
        >
          The file is not ready yet!!
        </Alert>
      </Snackbar>
      <Box
        sx={{
          flexGrow: 1,
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
          },
        }}
      >
        <DataGrid
          components={{
            Toolbar: QuickSearchToolbar,
          }}
          rows={homeTable} 
          columns={columns}
          density="standard"
          onRowClick={handleClick}
          loading={isLoading}
          hideFooterPagination 
          componentsProps={{
            toolbar: {
              value: localSearchText,
              onChange: (event) => setLocalSearchText(event.target.value),
              clearSearch: handleClearSearch,
              createButton: createButton,
              rows: homeTable,
              showMine: showMine,
              toggleShowMine: handleToggleShowMine,
              userId: userId,
            },
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: "1rem",
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {homeTable.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
          {Math.min(currentPage * pageSize, totalCount)} of {totalCount} entries
        </Typography>
        <FormControl size="small">
          <Select value={pageSize} onChange={handlePageSizeChange}>
            {[10, 25, 50, 100].map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Box>

      {open && (
        <ExportPopUp
          state={true}
          handleExportClose={handleExportClose}
          comicId={comicId}
        />
      )}
      
      <Dialog
        open={confirmOpen}
        onClose={() => {
          if (!confirmLoading) {
            setConfirmOpen(false);
            setRowToConfirm(null);
          }
        }}
      >
        <DialogTitle>Confirm</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to move back to the previous stage? This
            action will change the comic's processing stage.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              setRowToConfirm(null);
            }}
            disabled={confirmLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={confirmLoading}
          >
            {confirmLoading ? (
              <CircularProgress size={20} />
            ) : (
              "Yes, move it"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default HomeTable;
